import { CAPABILITY_IDS } from '@planuze/config';
import { Checkbox, Input, Label } from '@planuze/ui';
import { usePlansContent } from '../../content/sections/plans.js';

/* Editor de capabilities por plano (B3-S6, ADR-0419). Renderiza um controle por
   `CAPABILITY_IDS` (catálogo versionado); ausência da chave = não concedida
   (default-deny). Booleanas via Checkbox; quotas numéricas via Input (0/vazio =
   não concedida). O valor é `Record<CapabilityId, boolean|number>` = a coluna
   `plan_catalog.capabilities`. */

type Caps = Record<string, boolean | number>;

/* Capabilities com valor numérico (quota); o resto é flag booleana. */
const NUMERIC_CAPS: ReadonlySet<string> = new Set(['ai.tokens', 'history.retention', 'seats']);

interface Props {
  readonly value: Caps;
  readonly onChange: (next: Caps) => void;
  /* `seats` (teto de assentos) só faz sentido em plano de ORGANIZAÇÃO — a cobrança por
     assento é da org (ADR-0646). Em plano público/pessoal ou direcionado a usuário o
     campo não aparece (ADR-0650). Default `true` p/ não mudar outros usos. */
  readonly allowSeats?: boolean;
}

/* Capability restrita a plano de org — escondida quando `allowSeats` é falso. */
const ORG_ONLY_CAPS: ReadonlySet<string> = new Set(['seats']);

export function CapabilitiesEditor({ value, onChange, allowSeats = true }: Props) {
  const copy = usePlansContent();
  const setBool = (id: string, on: boolean): void => {
    const next = { ...value };
    if (on) {
      next[id] = true;
    } else {
      delete next[id];
    }
    onChange(next);
  };
  const setNum = (id: string, n: number): void => {
    const next = { ...value };
    if (n > 0) {
      next[id] = n;
    } else {
      delete next[id];
    }
    onChange(next);
  };
  return (
    <div className="space-y-2 rounded-md border border-border p-3">
      {CAPABILITY_IDS.filter((id) => allowSeats || !ORG_ONLY_CAPS.has(id)).map((id) => {
        const current = value[id];
        if (NUMERIC_CAPS.has(id)) {
          return (
            <div key={id} className="flex items-center justify-between gap-3">
              <Label htmlFor={`cap-${id}`}>{copy.capabilitiesEditor[id]}</Label>
              <Input
                id={`cap-${id}`}
                type="number"
                min={0}
                className="w-32"
                value={typeof current === 'number' ? String(current) : ''}
                onChange={(e) => setNum(id, Number.parseInt(e.target.value, 10) || 0)}
              />
            </div>
          );
        }
        return (
          <div key={id} className="flex items-center justify-between gap-3">
            <Label htmlFor={`cap-${id}`}>{copy.capabilitiesEditor[id]}</Label>
            <Checkbox
              id={`cap-${id}`}
              checked={current === true}
              onCheckedChange={(c) => setBool(id, c === true)}
            />
          </div>
        );
      })}
    </div>
  );
}
